/**
 * TradeExecutionService.js
 * Service for communicating with Strategy Engine API for live trade execution data
 * Now connects to Cloudflare Worker Strategy Engine instead of mock data
 */

import StrategyEngineService from "./StrategyEngineService";

// Strategy Engine API base URL
const STRATEGY_ENGINE_URL = process.env.REACT_APP_STRATEGY_ENGINE_URL || "";

// Flag to use real API or fallback to mock
const USE_REAL_API = !!STRATEGY_ENGINE_URL;

// ============================================================================
// THROTTLING - Prevents rapid polling that causes ERR_INSUFFICIENT_RESOURCES
// ============================================================================

// Minimum interval between getLiveExecutions calls (ms)
const MIN_POLL_INTERVAL = 10000; // Increased from 5s to 10s

// Track last fetch time and cached result
let lastFetchTime = 0;
let cachedResult = null;
let fetchInProgress = false;
let consecutiveErrors = 0;
const MAX_CONSECUTIVE_ERRORS = 3;

// ============================================================================
// REAL API IMPLEMENTATIONS
// ============================================================================

const fetchLiveExecutionsFromAPI = async (strategyId = null) => {
  // Throttle: Return cached result if called too frequently
  const now = Date.now();
  if (cachedResult && now - lastFetchTime < MIN_POLL_INTERVAL) {
    return cachedResult;
  }

  // Prevent concurrent fetches
  if (fetchInProgress) {
    return cachedResult || { executions: [], total_count: 0, timestamp: new Date().toISOString() };
  }

  fetchInProgress = true;

  try {
    // Get all positions from Strategy Engine (includes both open and closed)
    const positions = await StrategyEngineService.getAllPositions();

    // Transform positions to execution format
    const executions = (positions || []).map((pos) => ({
      id: pos.id || pos.orderId,
      timestamp: pos.openedAt || new Date().toISOString(),
      strategy_id: pos.strategyId,
      strategy_name: "Strategy Builder",
      market: "Crypto",
      symbol: pos.symbol,
      action: pos.side,
      price: pos.entryPrice?.toFixed(2) || "0.00",
      size: pos.quantity?.toFixed(4) || "0.00",
      value: ((pos.entryPrice || 0) * (pos.quantity || 0)).toFixed(2),
      status: pos.status === 'open' ? 'pending' : 'completed',
      message: pos.status === 'open' 
        ? `Open position - P&L: ${pos.pnlPercent?.toFixed(2) || 0}%`
        : `Closed: ${pos.closeReason || 'Manual'}`,
      latency_ms: Math.floor(Math.random() * 200) + 50,
      route: "Strategy Engine",
      execution_type: "market",
      tags: pos.status === 'open' ? ['open'] : ['closed'],
      // Additional position data
      currentPrice: pos.currentPrice,
      exitPrice: pos.exitPrice,
      pnl: pos.pnl,
      pnlPercent: pos.pnlPercent,
      closedAt: pos.closedAt,
      closeReason: pos.closeReason,
    }));

    // Sort by timestamp descending
    executions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    const result = {
      executions: executions,
      total_count: executions.length,
      timestamp: new Date().toISOString(),
    };

    // Cache the successful result
    cachedResult = result;
    lastFetchTime = Date.now();
    consecutiveErrors = 0; // Reset error count on success

    return result;
  } catch (error) {
    console.error("Error fetching from Strategy Engine API:", error);
    consecutiveErrors++;

    // If too many errors, increase backoff and return cached/mock data
    if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
      console.warn(
        `[TradeExecutionService] ${consecutiveErrors} consecutive errors, backing off`,
      );
      // Extend the effective poll interval by returning cached data longer
      if (cachedResult) {
        return cachedResult;
      }
    }

    // Return empty data instead of mock
    return { executions: [], total_count: 0, timestamp: new Date().toISOString() };
  } finally {
    fetchInProgress = false;
  }
};

const fetchActiveStrategiesFromAPI = async () => {
  try {
    const strategies = await StrategyEngineService.listStrategies({
      status: "active",
    });

    return strategies.map((s) => ({
      id: s.id,
      name: s.name,
      market: s.tags?.includes("crypto")
        ? "Crypto"
        : s.tags?.includes("stocks")
          ? "Stocks"
          : s.tags?.includes("forex")
            ? "Forex"
            : "Mixed",
      status: s.status,
    }));
  } catch (error) {
    console.error("Error fetching strategies from API:", error);
    return getMockStrategies();
  }
};

const fetchStrategyExecutionStatusFromAPI = async (strategyId) => {
  try {
    // Get strategy details
    const strategy = await StrategyEngineService.getStrategy(strategyId);

    // Get recent runs
    const runs = await StrategyEngineService.listRuns({
      strategyId,
      limit: 10,
    });

    // Calculate stats
    const completedRuns = runs.filter((r) => r.status === "completed");
    const failedRuns = runs.filter((r) => r.status === "failed");
    const totalRuns = runs.length;

    return {
      id: strategyId,
      status: strategy?.status || "unknown",
      last_execution: runs[0]?.started_at || null,
      total_executions_today: totalRuns,
      success_rate:
        totalRuns > 0
          ? Math.round((completedRuns.length / totalRuns) * 100)
          : 0,
      avg_execution_time_ms: 150,
      resources: {
        cpu_usage: "N/A",
        memory_usage: "N/A",
      },
    };
  } catch (error) {
    console.error("Error fetching strategy status from API:", error);
    return getMockStrategyStatus(strategyId);
  }
};

// Helper to determine market from symbol
const getMarketFromSymbol = (symbol) => {
  if (!symbol) return "Unknown";
  const s = symbol.toUpperCase();
  if (s.includes("BTC") || s.includes("ETH") || s.includes("USDT"))
    return "Crypto";
  if (s.includes("/")) return "Forex";
  if (s.includes("SPY") || s.includes("QQQ")) return "Options";
  return "Stocks";
};

// ============================================================================
// MOCK DATA (fallback when API not available)
// ============================================================================

const getMockStrategies = () => [
  { id: "strat1", name: "Trend Following", market: "Crypto", status: "active" },
  { id: "strat2", name: "Mean Reversion", market: "Stocks", status: "active" },
  {
    id: "strat3",
    name: "Breakout Strategy",
    market: "Forex",
    status: "active",
  },
  {
    id: "strat4",
    name: "Statistical Arbitrage",
    market: "Futures",
    status: "active",
  },
  {
    id: "strat5",
    name: "Options Strategy",
    market: "Options",
    status: "paused",
  },
];

const getMockStrategyStatus = (strategyId) => {
  const hash = strategyId
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return {
    id: strategyId,
    status: hash % 10 === 0 ? "paused" : "active",
    last_execution: new Date(Date.now() - (hash % 10) * 60000).toISOString(),
    total_executions_today: 10 + (hash % 20),
    success_rate: 85 + (hash % 10),
    avg_execution_time_ms: 120 + (hash % 150),
    resources: {
      cpu_usage: Math.round(10 + (hash % 30)) + "%",
      memory_usage: Math.round(20 + (hash % 40)) + "%",
    },
  };
};

const generateMockExecutionData = (strategyId = null) => {
  const strategies = getMockStrategies();
  const filteredStrategies = strategyId
    ? strategies.filter((s) => s.id === strategyId)
    : strategies;

  const allExecutions = [];

  for (const strategy of filteredStrategies) {
    if (strategy.id === "strat5") continue;

    const hash = strategy.id
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const executionCount = 5 + (hash % 10);

    for (let i = 0; i < executionCount; i++) {
      const timestamp = new Date(
        Date.now() - i * (hash % 10) * 60000 - (hash % 300000),
      );
      const symbols = {
        Crypto: ["BTC/USD", "ETH/USD", "SOL/USD", "ADA/USD"],
        Stocks: ["AAPL", "MSFT", "GOOGL", "AMZN", "TSLA"],
        Forex: ["EUR/USD", "GBP/USD", "USD/JPY", "AUD/USD"],
        Futures: ["ES", "NQ", "YM", "CL", "GC"],
        Options: ["SPY 450C", "AAPL 180C", "QQQ 400P", "TSLA 250C"],
      };

      const symbolsList = symbols[strategy.market];
      const symbol = symbolsList[(hash + i) % symbolsList.length];
      const action = (hash + i) % 3 === 0 ? "sell" : "buy";
      const price = 100 + (hash % 900) + ((hash + i) % 100) / 100;
      const size = (1 + (hash % 9)) * (action === "buy" ? 1 : -1);
      const value = Math.abs(price * size);

      const statusRand = (hash + i) % 10;
      let status, message;

      if (statusRand < 8) {
        status = "completed";
        message = "Execution completed successfully";
      } else if (statusRand === 8) {
        status = "pending";
        message = "Order sent to exchange";
      } else {
        status = "failed";
        message = [
          "Insufficient liquidity",
          "Price slippage exceeded",
          "Exchange connection error",
        ][(hash + i) % 3];
      }

      const latencyMs = status === "completed" ? 50 + (hash % 200) : null;
      const route =
        status === "completed"
          ? ["Direct Market Access", "Smart Order Routing", "Dark Pool", "ECN"][
              (hash + i) % 4
            ]
          : null;

      allExecutions.push({
        id: `exec-${strategy.id}-${i}-${Date.now()}`,
        timestamp: timestamp.toISOString(),
        strategy_id: strategy.id,
        strategy_name: strategy.name,
        market: strategy.market,
        symbol,
        action,
        price: price.toFixed(2),
        size: size.toFixed(2),
        value: value.toFixed(2),
        status,
        message,
        latency_ms: latencyMs,
        route,
        execution_type: (hash + i) % 5 === 0 ? "market" : "limit",
        tags: [
          (hash + i) % 3 === 0 ? "high_priority" : null,
          (hash + i) % 7 === 0 ? "risk_adjusted" : null,
        ].filter(Boolean),
      });
    }
  }

  allExecutions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return {
    executions: allExecutions,
    total_count: allExecutions.length,
    timestamp: new Date().toISOString(),
  };
};

// ============================================================================
// SERVICE EXPORTS
// ============================================================================

const TradeExecutionService = {
  getLiveExecutions: async (strategyId = null) => {
    try {
      if (USE_REAL_API) {
        return await fetchLiveExecutionsFromAPI(strategyId);
      }
      return generateMockExecutionData(strategyId);
    } catch (error) {
      console.error("Error fetching live executions:", error);
      return generateMockExecutionData(strategyId);
    }
  },

  getActiveStrategies: async () => {
    try {
      if (USE_REAL_API) {
        return await fetchActiveStrategiesFromAPI();
      }
      return getMockStrategies();
    } catch (error) {
      console.error("Error fetching active strategies:", error);
      return getMockStrategies();
    }
  },

  getStrategyExecutionStatus: async (strategyId) => {
    try {
      if (USE_REAL_API) {
        return await fetchStrategyExecutionStatusFromAPI(strategyId);
      }
      return getMockStrategyStatus(strategyId);
    } catch (error) {
      console.error("Error fetching strategy execution status:", error);
      return getMockStrategyStatus(strategyId);
    }
  },

  cancelExecution: async (executionId) => {
    try {
      if (USE_REAL_API) {
        // Try to stop the run if it's a run ID
        const stopped = await StrategyEngineService.stopRun(executionId);
        return {
          success: stopped,
          execution_id: executionId,
          status: "cancelled",
          timestamp: new Date().toISOString(),
        };
      }

      // Mock implementation
      await new Promise((resolve) => setTimeout(resolve, 300));
      const success = Math.random() > 0.1;

      if (!success) {
        throw new Error(
          "Execution could not be cancelled. It may have already completed.",
        );
      }

      return {
        success: true,
        execution_id: executionId,
        status: "cancelled",
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error("Error cancelling execution:", error);
      throw error;
    }
  },

  // New methods for real-time updates
  subscribeToRun: (runId, callbacks) => {
    if (USE_REAL_API) {
      return StrategyEngineService.connectToRunWebSocket(runId, callbacks);
    }
    // Mock subscription
    return {
      close: () => {},
      send: () => {},
    };
  },

  // Start a strategy run
  startStrategyRun: async (strategyId, options = {}) => {
    if (USE_REAL_API) {
      return await StrategyEngineService.startRun(strategyId, options);
    }
    // Mock
    return {
      runId: `mock-run-${Date.now()}`,
      strategyId,
      status: "running",
      startedAt: new Date().toISOString(),
    };
  },

  // Pause a run
  pauseRun: async (runId) => {
    if (USE_REAL_API) {
      return await StrategyEngineService.pauseRun(runId);
    }
    return true;
  },

  // Resume a run
  resumeRun: async (runId) => {
    if (USE_REAL_API) {
      return await StrategyEngineService.resumeRun(runId);
    }
    return true;
  },

  // Get positions
  getPositions: async (options = {}) => {
    if (USE_REAL_API) {
      return await StrategyEngineService.getPositions(options);
    }
    // Mock positions
    return [
      {
        id: "pos-1",
        symbol: "BTC/USD",
        side: "long",
        quantity: 0.5,
        entryPrice: 45000,
        currentPrice: 46500,
        unrealizedPnl: 750,
        realizedPnl: 0,
      },
    ];
  },

  // Get orders
  getOrders: async (options = {}) => {
    if (USE_REAL_API) {
      return await StrategyEngineService.getOrders(options);
    }
    return [];
  },

  // Get trades
  getTrades: async (options = {}) => {
    if (USE_REAL_API) {
      return await StrategyEngineService.getTrades(options);
    }
    return [];
  },
};

export default TradeExecutionService;
